import { Fragment } from 'react';
import i18n from 'helpers/i18n';
import colors from 'styles/colors';
import { SubtitleSection, Section } from 'components/Reports/Reliability/View';
import messages from './messages';
import DimensionResults, {
  DimensionReliability,
  DimensionPreparation
} from './DimensionResults';

export type TestType = 'reliability' | 'preparation';

export type DomainReliability = {
  name: string;
  score: number;
  dimensions: DimensionReliability[];
};

export type DomainPreparation = {
  name: string;
  dimensions: DimensionPreparation[];
};

interface Reliability {
  testType: 'reliability';
  scores: number[];
  domains: DomainReliability[];
}

interface Preparation {
  testType: 'preparation';
  domains: DomainPreparation[];
}

function isReliability(
  domain: DomainReliability | DomainPreparation
): domain is DomainReliability {
  return (domain as DomainReliability).score !== undefined;
}

function DimensionSection(props: Reliability | Preparation) {
  const { domains, testType } = props;
  const numVideos =
    testType === 'reliability' ? (props as Reliability).scores.length : 0;

  return (
    <Section className="PrintBackgroundColor PrintPageBreak">
      <SubtitleSection className="justify-center md:justify-start PrintLeftTitle">
        {i18n.ft(messages.title)}
      </SubtitleSection>
      <table className="w-full border-collapse text-sm md:text-base">
        <thead>
          {testType !== 'preparation' ? (
            <>
              <tr>
                <th
                  rowSpan={2}
                  scope="col"
                  className="py-2 font-bold text-left"
                >
                  {i18n.ft(messages.columnTitle)}
                </th>
                <th
                  rowSpan={2}
                  scope="col"
                  className="text-center text-black border border-white text-sm lg:text-xl"
                  style={{ backgroundColor: colors.scaleSection }}
                >
                  <span className="text-xs font-bold block mx-auto md:text-sm">
                    <span className="hidden md:inline">
                      {i18n.ft(messages.overall)}{' '}
                    </span>
                    %
                  </span>
                </th>
                <th
                  colSpan={numVideos}
                  scope="colgroup"
                  className="text-center font-bold pb-2 PrintMargin"
                >
                  {i18n.ft(messages.videos)}
                </th>
              </tr>
              <tr>
                {(props as Reliability).scores.map(
                  (score: number, idx: number) => (
                    <th
                      key={idx}
                      id={`video-header-${idx}`}
                      scope="col"
                      className="text-center py-2"
                      aria-label={`${i18n.ft(messages.video)} ${idx + 1}`}
                    >
                      <div
                        className="rounded-full w-6 h-6 md:w-11 md:h-11 border-2 md:border-[6px] flex items-center justify-center mx-auto text-sm md:text-lg font-bold PrintMarginLeft"
                        style={{
                          borderColor:
                            score >= 80
                              ? colors.reliability.passVideo
                              : colors.reliability.noPassVideo
                        }}
                        aria-hidden="true"
                      >
                        {idx + 1}
                      </div>
                    </th>
                  )
                )}
              </tr>
            </>
          ) : (
            <tr>
              <th scope="col" className="py-2 font-bold text-left pl-2">
                {i18n.ft(messages.columnTitle)}
              </th>
              <th scope="col" className="py-2 font-bold text-center">
                {i18n.ft(messages.reliable)}
              </th>
            </tr>
          )}
        </thead>
        <tbody>
          {domains.map(
            (domain: DomainReliability | DomainPreparation, idx: number) => (
              <Fragment key={idx}>
                <tr
                  className="text-sm md:text-xl"
                  style={{ backgroundColor: '#D5E0F3' }}
                >
                  <th
                    scope="row"
                    className={`${
                      !isReliability(domain) ? 'pl-8' : ''
                    } py-4 font-bold pl-2 text-base sm:my-2`}
                  >
                    {domain.name}
                  </th>
                  {isReliability(domain) ? (
                    <>
                      <td className="flex items-center justify-center py-3">
                        <div className="bg-white px-4 py-1 text-sm h-8 text-center text-black rounded-full font-bold border border-white md:text-base inline-flex items-center justify-center">
                          {domain.score}%
                        </div>
                      </td>
                      {Array.from({ length: numVideos }, (_, i) => (
                        <td key={i}></td>
                      ))}
                    </>
                  ) : (
                    <td></td>
                  )}
                </tr>
                {isReliability(domain) ? (
                  <DimensionResults dimensions={domain.dimensions} />
                ) : (
                  <DimensionResults dimensions={domain.dimensions} />
                )}
              </Fragment>
            )
          )}
        </tbody>
      </table>
      {testType !== 'preparation' ? (
        <div className="mx-1 mt-2 text-center">{i18n.ft(messages.note)}</div>
      ) : null}
    </Section>
  );
}

export default DimensionSection;
